import { useForm } from "@inertiajs/react"
import { type FormEvent, useEffect } from "react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { listPath } from "@/routes"
import type { List } from "@/types"

interface EditListModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  list: List
}

export function EditListModal({
  open,
  onOpenChange,
  list,
}: EditListModalProps) {
  const form = useForm({
    list: {
      name: list.name,
      auto_archive_days: list.auto_archive_days ?? 7,
      auto_clean_days: list.auto_clean_days ?? 7,
    },
  })

  useEffect(() => {
    form.setData({
      list: {
        name: list.name,
        auto_archive_days: list.auto_archive_days ?? 7,
        auto_clean_days: list.auto_clean_days ?? 7,
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list])

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    form.patch(listPath(list.id), {
      preserveScroll: true,
      onSuccess: () => {
        onOpenChange(false)
      },
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>LIST SETTINGS</DialogTitle>
            <DialogDescription>
              Configure the rules of life and death for this list
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label
                htmlFor="edit-list-name"
                className="font-mono text-xs tracking-wider uppercase"
              >
                List Name
              </Label>
              <Input
                id="edit-list-name"
                value={form.data.list.name}
                onChange={(e) =>
                  form.setData("list", {
                    ...form.data.list,
                    name: e.target.value,
                  })
                }
                placeholder="List name..."
                autoComplete="off"
                autoFocus
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label
                  htmlFor="auto-archive-days"
                  className="font-mono text-xs tracking-wider uppercase"
                >
                  Time to Live
                </Label>
                <Select
                  value={form.data.list.auto_archive_days.toString()}
                  onValueChange={(value) =>
                    form.setData("list", {
                      ...form.data.list,
                      auto_archive_days: parseInt(value),
                    })
                  }
                >
                  <SelectTrigger id="auto-archive-days" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14].map(
                      (days) => (
                        <SelectItem key={days} value={days.toString()}>
                          {days} {days === 1 ? "day" : "days"}
                        </SelectItem>
                      ),
                    )}
                  </SelectContent>
                </Select>
                <p className="text-bone-muted text-xs">
                  How long tasks stay alive
                </p>
              </div>
              <div className="space-y-2">
                <Label
                  htmlFor="auto-clean-days"
                  className="font-mono text-xs tracking-wider uppercase"
                >
                  Burial
                </Label>
                <Select
                  value={form.data.list.auto_clean_days.toString()}
                  onValueChange={(value) =>
                    form.setData("list", {
                      ...form.data.list,
                      auto_clean_days: parseInt(value),
                    })
                  }
                >
                  <SelectTrigger id="auto-clean-days" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14].map(
                      (days) => (
                        <SelectItem key={days} value={days.toString()}>
                          {days} {days === 1 ? "day" : "days"}
                        </SelectItem>
                      ),
                    )}
                  </SelectContent>
                </Select>
                <p className="text-bone-muted text-xs">
                  When dead tasks are buried
                </p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={form.processing}>
              Save Changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
